import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { COLORS } from '../../../../constants/booking';

interface StaffStats {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
  onDuty: number;
  available: number;
}

interface StaffSummaryCardsProps {
  staffStats: StaffStats;
}

const StaffSummaryCards: React.FC<StaffSummaryCardsProps> = ({ staffStats }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Helper function to create donut chart SVG
  const createDonutChart = (percentage: number, color: string, size: number = 40) => {
    const radius = size / 2 - 3;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;
    
    return (
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={isDark ? '#334155' : '#e2e8f0'}
          strokeWidth="4"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
    );
  };

  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {/* Staff Summary Card */}
      <div
        style={{
          flex: '1',
          minWidth: '200px',
          backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
          borderRadius: '6px',
          padding: '10px',
          border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <div style={{ flexShrink: 0 }}>
          {createDonutChart(
            staffStats.total > 0 ? (staffStats.onDuty / staffStats.total) * 100 : 0,
            '#ef4444',
            40
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: '12px',
              fontWeight: '600',
              color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
              marginBottom: '2px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Staff
          </div>
          <div
            style={{
              fontSize: '26px',
              fontWeight: '700',
              color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
              marginBottom: '4px',
              lineHeight: '1',
            }}
          >
            {staffStats.total}
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ color: '#10b981', fontSize: '15px', fontWeight: '600' }}>
                {staffStats.available}
              </span>
              <span style={{ color: '#ef4444', fontSize: '15px', fontWeight: '600' }}>
                {staffStats.onDuty}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: '13px' }}>Available</span>
              <span style={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: '13px' }}>On Duty</span>
            </div>
          </div>
        </div>
      </div>

      {/* Status Breakdown Card */}
      <div
        style={{
          flex: '1',
          minWidth: '200px',
          backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
          borderRadius: '6px',
          padding: '10px',
          border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}
      >
        <div
          style={{
            fontSize: '12px',
            fontWeight: '600',
            color: isDark ? '#94a3b8' : '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Status
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
              }}
            />
            <span style={{ color: isDark ? '#cbd5e1' : '#475569', fontSize: '14px' }}>Active</span>
          </div>
          <span style={{ color: isDark ? '#f1f5f9' : '#0f172a', fontWeight: '600', fontSize: '15px' }}>
            {staffStats.active}
          </span>
        </div>
      </div>
    </div>
  );
};

export default StaffSummaryCards;

